import { authSplitApi } from 'redux/helpers/slice.helpers';
import { animalApi } from 'redux/apiSlice/animal.slice';
import {
  EApplicationUrls,
  COMMON_PROPERTY_UNITS_URL,
} from 'data/urls/application.url';
import {
  EApplicationStepTypeUi,
  TApplicableProperties,
  TApplicablePropertiesUI,
  TApplicationDetailDB,
  TApplicationDetailUI,
  TApplicationSignUI,
  TApplicationStep,
  TApplicationStepUi,
  TLeasePacketUnit,
  TLeasePacketUnitUi,
  TPaymentDetail,
  TPaymentDetailUi,
  TPMStepDetails,
  TPMStepDetailsUI,
  TRetractApplicationUI,
  TStepVerification,
  TStepVerificationParams,
  TStepVerificationUI,
} from 'data/types/application.types';
import {
  applicationStepTypeConvertor,
  getApplicationDetailConvertor,
  getApplicationSteps,
  getLeasePacketPropertyConverter,
  getLeasePacketUnitConverter,
  getPaymentDetailConverter,
  pmStepDetailsConvertor,
  retractApplicationConverter,
  signApplicationConvertor,
  stepVerificationConvertor,
} from 'data/convertors/application.converters';
import { TSetApplicantsMoveInDateUI } from 'data/types/residency.types';
import { setApplicantsMoveInDateConverter } from 'data/convertors/residency.convertor';

const baseUrl = `${EApplicationUrls.APPLICATION}`;

export const applicationsApi = authSplitApi('application', [
  'application',
  'applications',
  'steps',
  'leasePackets',
  'payment',
  'leasePacketProperties',
  'pmStepDetails',
  'propertyManagements',
  'leasePacketUnits',
]).injectEndpoints({
  endpoints: build => ({
    getApplicationSteps: build.query<
      TApplicationStepUi[],
      { applicationId: string }
    >({
      query({ applicationId }) {
        return {
          url: `${baseUrl}/${EApplicationUrls.STEP}`,
          method: 'GET',
          params: {
            applicationId,
          },
        };
      },
      transformResponse: (data: TApplicationStep[]) =>
        data.map(item => getApplicationSteps.fromDb(item)),
      providesTags: ['steps'],
    }),
    getPMStepDetails: build.query<
      TPMStepDetailsUI,
      { stepType: EApplicationStepTypeUi }
    >({
      query({ stepType }) {
        const type = applicationStepTypeConvertor.toDb(stepType);
        return {
          url: `${baseUrl}/${EApplicationUrls.STEP}/${EApplicationUrls.DETAILS}`,
          method: 'GET',
          params: {
            stepType: type,
          },
        };
      },
      transformResponse: (data: TPMStepDetails) =>
        pmStepDetailsConvertor.fromDb(data),
      providesTags: ['pmStepDetails'],
    }),
    getApplicationDetail: build.query<
      TApplicationDetailUI,
      {
        applicationId: string;
        animalId: string;
        animalResidencyId?: string;
      }
    >({
      query({ applicationId, animalId, animalResidencyId }) {
        return {
          url: `${baseUrl}/${applicationId}`,
          method: 'GET',
          params: { animalId, animalResidencyId },
        };
      },
      transformResponse: (data: TApplicationDetailDB) =>
        getApplicationDetailConvertor.fromDb(data),
      providesTags: ['application'],
    }),
    getStepVerification: build.mutation<
      TStepVerificationUI,
      TStepVerificationParams
    >({
      query({ applicationId, type }) {
        return {
          url: `${baseUrl}/${EApplicationUrls.STEP}/${EApplicationUrls.VERIFY}`,
          method: 'GET',
          params: {
            applicationId,
            type: applicationStepTypeConvertor.toDb(type),
          },
        };
      },
      transformResponse: (data: TStepVerification) =>
        stepVerificationConvertor.fromDb(data),
    }),
    signApplication: build.mutation<string, TApplicationSignUI>({
      query(data) {
        return {
          url: `${baseUrl}/${EApplicationUrls.SIGN}`,
          method: 'PATCH',
          body: signApplicationConvertor.toDb(data),
        };
      },
      invalidatesTags: ['application'],
    }),
    getLeasePacketProperties: build.query<
      TApplicablePropertiesUI,
      { accessCodeId: string; state?: string }
    >({
      query({ accessCodeId, state }) {
        return {
          url: `${EApplicationUrls.ACCESS_CODE}/${EApplicationUrls.PROPERTIES}`,
          method: 'GET',
          params: { accessCodeId, state },
        };
      },
      transformResponse: (data: TApplicableProperties) =>
        getLeasePacketPropertyConverter.fromDb(data),
      providesTags: ['leasePacketProperties'],
    }),
    getPaymentDetails: build.query<
      TPaymentDetailUi,
      { animalId: string; applicationId: string }
    >({
      query({ applicationId, animalId }) {
        return {
          url: `${baseUrl}/${EApplicationUrls.STEP}/${EApplicationUrls.PAYMENT}`,
          method: 'GET',
          params: {
            applicationId,
            animalId,
          },
        };
      },
      transformResponse: (data: TPaymentDetail) =>
        getPaymentDetailConverter.fromDb(data),
      providesTags: ['payment'],
    }),
    getLeasePacketUnits: build.query<
      TLeasePacketUnitUi[],
      { propertyId: string }
    >({
      query({ propertyId }) {
        return {
          url: COMMON_PROPERTY_UNITS_URL,
          method: 'GET',
          params: {
            propertyId,
          },
        };
      },
      transformResponse: (data: TLeasePacketUnit[]) =>
        data.map(item => getLeasePacketUnitConverter.fromDb(item)),
      providesTags: ['leasePacketUnits'],
    }),
    retractApplication: build.mutation<void, TRetractApplicationUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.RETRACT}`,
          method: 'PATCH',
          body: retractApplicationConverter.toDb(body),
        };
      },
      // Retracting changes the animal lists and what is left to complete, but those caches live in
      // animalApi, whose tags this slice cannot declare. Done here rather than at the call sites so
      // no caller can forget — one of the three already did, leaving the home-page banner stale.
      // The residency list follows on its own, mirrored from the animal invalidation by the store.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        dispatch(
          animalApi.util.invalidateTags([
            'animal',
            'applicationAnimal',
            'animals',
            'incompleteAnimals',
          ]),
        );
      },
    }),
    confirmPrivacyPolicy: build.mutation<
      void,
      { applicationId: string; animalId: string }
    >({
      query(body) {
        return {
          url: `${baseUrl}/${EApplicationUrls.CONFIRM_PRIVACY_POLICY}`,
          method: 'PATCH',
          body,
        };
      },
    }),
    setApplicantsMoveInDate: build.mutation<void, TSetApplicantsMoveInDateUI[]>(
      {
        query(data) {
          return {
            url: `${baseUrl}/${EApplicationUrls.MOVE_IN_DATE}`,
            method: 'PATCH',
            body: {
              applicationMoveInDateDetails: (data || []).map(application =>
                setApplicantsMoveInDateConverter.toDb(application),
              ),
            },
          };
        },
      },
    ),
  }),
});

export const {
  useGetApplicationStepsQuery,
  useRetractApplicationMutation,
  useGetPaymentDetailsQuery,
  useGetStepVerificationMutation,
  useGetApplicationDetailQuery,
  useGetPMStepDetailsQuery,
  useSignApplicationMutation,
  useGetLeasePacketPropertiesQuery,
  useGetLeasePacketUnitsQuery,
  useConfirmPrivacyPolicyMutation,
  useSetApplicantsMoveInDateMutation,
} = applicationsApi;
